import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { baseAPI } from '../../config';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ModalBasic from '../ModalBasic';
import ExpedientsPermissionSwitch from './ExpedientsPermissionSwitch';
import DeleteOperator from './DeleteOperator';
import { UsersIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import userStore from '../../store/userStore';
import { TEAM_ROLES, ROLE_LABELS } from '@claimup/shared/constants/roles';
import { COMPANY_SERVICE_TYPES } from '@claimup/shared/constants/company';

const ROLE_DESCRIPTIONS = {
  operator: 'Operador (ve y anota, no comunica)',
  gestor: 'Gestor (recibe tareas y comunica con deudores)',
};

const OperatorsManager = ({ userData }) => {
  // Solo las companies AR pueden tener gestores; en el resto, solo 'operator'.
  const isARCompany =
    userData?.service_type === COMPANY_SERVICE_TYPES.ACCOUNT_RECEIVABLE;
  const availableRoles = isARCompany
    ? TEAM_ROLES
    : TEAM_ROLES.filter(r => r !== 'gestor');
  const navigate = useNavigate();
  const operators = userStore(state => state.operators);
  const setOperators = userStore(state => state.setOperators);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    lastname: '',
    email: '',
    phone: '',
    role: 'operator',
  });

  // gestor: como operator pero recibe assignments y comunica con deudores (AR).
  const roleLabel = r => ROLE_LABELS[r] || ROLE_LABELS.operator;

  const fetchOperators = useCallback(async () => {
    if (!userData?._id) return;

    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const response = await axios.get(`${baseAPI}/users?company=${userData?._id}`, { headers });
      // Always update operators, even if it's an empty array
      setOperators(response.data || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching operators:', error);
      toast.error('Error al cargar los usuarios', {
        position: 'top-right',
        autoClose: 3000,
      });
      setIsLoading(false);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    }
  }, [navigate, userData?._id, setOperators]);

  useEffect(() => {
    // Add a small delay to ensure store is hydrated
    const timer = setTimeout(() => {
      fetchOperators();
    }, 100);

    return () => clearTimeout(timer);
  }, [fetchOperators]);

  const handleInputChange = e => {
    let { name, value } = e.target;
    if (name === 'phone') {
      value = value.replace(/\s/g, '');
    }
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      if (isEditMode) {
        // Update existing user
        const updateData = {
          username: formData.name,
          userlastname: formData.lastname,
          userphone: formData.phone,
          userrole: formData.role,
        };

        await axios.post(`${baseAPI}/users/update/${editingUser._id}`, updateData, { headers });

        toast.success('Usuario actualizado correctamente', {
          position: 'top-right',
          autoClose: 2000,
        });
      } else {
        // Create new user
        const newOperator = {
          ...formData,
          company_id: userData?._id,
        };

        await axios.post(`${baseAPI}/users/add-operator`, newOperator, { headers });

        toast.success('Usuario creado correctamente', {
          position: 'top-right',
          autoClose: 2000,
        });
      }

      // Reset form and close modal
      closeModal();

      // Refresh the operators list
      fetchOperators();
    } catch (error) {
      console.error('Error creating/updating user:', error);
      toast.error(error.response?.data?.message || `Error al ${isEditMode ? 'actualizar' : 'crear'} el usuario`, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openModal = e => {
    e.preventDefault();
    e.stopPropagation();

    // Nuevo usuario: partir siempre de un formulario vacío (aunque el modal se
    // hubiera cerrado por la X/backdrop, que no pasa por closeModal).
    setIsEditMode(false);
    setEditingUser(null);
    setFormData({
      name: '',
      lastname: '',
      email: '',
      phone: '',
      role: 'operator',
    });

    // Add a small delay to prevent race conditions
    setTimeout(() => {
      setIsModalOpen(true);
    }, 10);
  };

  const openEditModal = user => {
    setIsEditMode(true);
    setEditingUser(user);
    setFormData({
      name: user.username || '',
      lastname: user.userlastname || '',
      email: user.usermail || '',
      phone: user.userphone || '',
      role: user.userrole || 'operator',
    });

    // Add a small delay to prevent race conditions
    setTimeout(() => {
      setIsModalOpen(true);
    }, 10);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingUser(null);
    setFormData({
      name: '',
      lastname: '',
      email: '',
      phone: '',
      role: 'operator',
    });
  };

  if (isLoading) {
    return (
      <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
        <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center'>
              <UsersIcon className='h-5 w-5 text-blue-600 mr-2' />
              <h3 className='text-lg font-medium text-slate-900'>Usuarios</h3>
            </div>
            <button
              onClick={openModal}
              className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors duration-150'
            >
              <PlusIcon className='h-4 w-4 mr-1' />
              Nuevo Usuario
            </button>
          </div>
        </div>
        <div className='p-6'>
          <div className='flex items-center justify-center py-8'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500'></div>
          </div>
        </div>
      </div>
    );
  }

  // Opciones del select: los roles disponibles + el rol actual del que se edita
  // (evita que, al editar un gestor en una company que ya no es AR, el select
  // muestre 'operator' mientras el estado sigue en 'gestor').
  const roleOptions = availableRoles.includes(formData.role)
    ? availableRoles
    : [...availableRoles, formData.role];

  return (
    <>
      <div className='bg-white rounded-lg border border-slate-200 overflow-hidden'>
        <div className='px-6 py-4 border-b border-slate-200 bg-slate-50'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center'>
              <UsersIcon className='h-5 w-5 text-blue-600 mr-2' />
              <h3 className='text-lg font-medium text-slate-900'>Usuarios</h3>
            </div>
            <button
              onClick={openModal}
              className='inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors duration-150'
            >
              <PlusIcon className='h-4 w-4 mr-1' />
              Nuevo Usuario
            </button>
          </div>
        </div>

        <div className='p-6'>
          {/* Switch Control */}
          <ExpedientsPermissionSwitch userData={userData} isLoading={isSubmitting} />

          {/* Users List */}
          {operators.length === 0 ? (
            <div className='text-center text-slate-500 py-8'>
              <UsersIcon className='h-12 w-12 mx-auto mb-4 text-slate-300' />
              <p className='text-sm'>No hay usuarios</p>
            </div>
          ) : (
            <div className='overflow-x-auto'>
              <table className='min-w-full divide-y divide-slate-200'>
                <thead className='bg-slate-50'>
                  <tr>
                    <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                      Usuario
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                      Rol
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                      Email
                    </th>
                    <th className='px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider'>
                      Teléfono
                    </th>
                    <th className='px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider'>
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className='bg-white divide-y divide-slate-200' key={operators.length}>
                  {operators.map(operator => (
                    <tr key={operator._id} className='hover:bg-slate-50'>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <div className='flex items-center'>
                          <div className='flex-shrink-0 h-10 w-10'>
                            <div className='h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center'>
                              <span className='text-sm font-medium text-blue-600'>
                                {operator.username?.charAt(0)?.toUpperCase() || 'U'}
                              </span>
                            </div>
                          </div>
                          <div className='ml-4'>
                            <div className='text-sm font-medium text-slate-900'>
                              {operator.username} {operator.userlastname}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap'>
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-lg ${
                            operator.userrole === 'gestor'
                              ? 'bg-purple-100 text-purple-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {roleLabel(operator.userrole)}
                        </span>
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-slate-900'>{operator.usermail}</td>
                      <td className='px-6 py-4 whitespace-nowrap text-sm text-slate-900'>
                        {operator.userphone || '-'}
                      </td>
                      <td className='px-6 py-4 whitespace-nowrap text-right text-sm font-medium'>
                        <div className='flex items-center justify-end space-x-2'>
                          <button
                            onClick={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              openEditModal(operator);
                            }}
                            className='text-blue-600 hover:text-blue-900'
                            title='Editar'
                          >
                            <PencilIcon className='h-4 w-4' />
                          </button>

                          <DeleteOperator
                            operatorToDelete={operator}
                            company={userData}
                            operators={operators}
                            fetchOperators={fetchOperators}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* New User Modal */}
      <ModalBasic
        id='new-user-modal'
        title={isEditMode ? 'Editar Usuario' : 'Nuevo Usuario'}
        modalOpen={isModalOpen}
        setModalOpen={setIsModalOpen}
      >
        <div className='px-6 py-5'>
          <form onSubmit={handleSubmit} className='space-y-4'>
            <div>
              <label htmlFor='name' className='block text-sm font-medium text-gray-700 mb-1'>
                Nombre *
              </label>
              <input
                type='text'
                id='name'
                name='name'
                value={formData.name}
                onChange={handleInputChange}
                required
                className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                placeholder='John'
              />
            </div>

            <div>
              <label htmlFor='lastname' className='block text-sm font-medium text-gray-700 mb-1'>
                Apellido *
              </label>
              <input
                type='text'
                id='lastname'
                name='lastname'
                value={formData.lastname}
                onChange={handleInputChange}
                required
                className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                placeholder='Doe'
              />
            </div>

            <div>
              <label htmlFor='email' className='block text-sm font-medium text-gray-700 mb-1'>
                Email *
              </label>
              <input
                type='email'
                id='email'
                name='email'
                value={formData.email}
                onChange={handleInputChange}
                required
                readOnly={isEditMode}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 ${
                  isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''
                }`}
                placeholder='john.doe@example.com'
              />
              {isEditMode && <p className='text-xs text-gray-500 mt-1'>El email no se puede modificar</p>}
            </div>

            <div>
              <label htmlFor='phone' className='block text-sm font-medium text-gray-700 mb-1'>
                Teléfono *
              </label>
              <input
                type='tel'
                id='phone'
                name='phone'
                value={formData.phone}
                onChange={handleInputChange}
                required
                className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                placeholder='600 00 00 00'
              />
            </div>

            <div>
                <label htmlFor='role' className='block text-sm font-medium text-gray-700 mb-1'>
                  Rol *
                </label>
                <select
                  id='role'
                  name='role'
                  value={formData.role}
                  onChange={handleInputChange}
                  className='w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                >
                  {roleOptions.map(r => (
                    <option key={r} value={r}>
                      {ROLE_DESCRIPTIONS[r] || ROLE_LABELS[r]}
                    </option>
                  ))}
                </select>
                <div className='mt-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1'>
                  <p>
                    <span className='font-medium text-slate-700'>Operador:</span> ve los expedientes
                    que tiene asignados y puede crear y responder anotaciones. No se comunica con los
                    deudores ni recibe tareas.
                  </p>
                  {isARCompany && (
                    <p>
                      <span className='font-medium text-slate-700'>Gestor:</span> además de lo anterior,
                      recibe tareas (assignments) y puede comunicarse con los deudores.
                    </p>
                  )}
                </div>
              </div>

            <div className='flex justify-end space-x-3 pt-4'>
              <button
                type='button'
                onClick={closeModal}
                className='px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500'
              >
                Cancelar
              </button>
              <button
                type='submit'
                disabled={isSubmitting}
                className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {isSubmitting
                  ? isEditMode
                    ? 'Actualizando...'
                    : 'Creando...'
                  : isEditMode
                    ? 'Actualizar Usuario'
                    : 'Crear Usuario'}
              </button>
            </div>
          </form>
        </div>
      </ModalBasic>
    </>
  );
};

export default OperatorsManager;
